<template>
	<div class="file-page page">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<p class="file-page__title mb-5">
				Details
			</p>
			<v-skeleton-loader
				style="max-width: 600px;"
				v-bind="attrs"
				type="card"
				v-if="this.isLoading"
			>
			</v-skeleton-loader>
			<div class="file-page__body" v-else-if="resource">
				<div class="file-page__body-header">
					<div v-if="resourceImage" class="file-page__body-header-img-wrapper">
						<img
							:src="resource.resource_image"
							class="file-page__body-header-img mr-3"
							alt="resource image"
						/>
					</div>
					<div v-else class="file-page__body-header-icon-wrapper pl-6 mr-3">
						<ResourceFileIcon
							:fileName="resource.upload.file_real_name"
							:url="resource.upload.url"
						/>
					</div>
					<div class="d-flex flex-column">
						<p class="file-page__info-filename">
							{{ resource.resources_name }}
						</p>
						<p class="file-page__info-author" v-if="resource.resource_author">
							{{ resource.resource_author }}
						</p>
						<div class="d-flex align-end" style="flex-grow: 1;">
							<a :href="resource.upload.url" target="_blank">
								<v-btn class="file-page__info-btn elevation-0">
									Open
								</v-btn>
							</a>
						</div>
					</div>
				</div>
				<div class="file-page__body-content mt-2">
					<div class="file-page__body-chips" v-if="resource.tags.length">
						<v-chip
							v-for="tag in resource.tags"
							:key="tag.tag_id"
							class="file-page__body-chips-item"
							:style="{color: tag.tag_txt_color, background: tag.tag_bg_color}"
						>
							{{ '#' + tag.tag }}
						</v-chip>
					</div>
					<p v-if="resource.resource_text" class="file-page__text">
						{{ resource.resource_text }}
					</p>
					<div class="file-page__dates" v-if="datesSectionVisible">
						<p v-if="resourceUploadDate">
                            <span class="file-page__dates-title">
                                Uploaded:
                            </span>
							<span class="file-page__dates-value">
                                {{ resourceUploadDate }}
                            </span>
						</p>
						<v-divider
							v-if="resourceUploadDate && resourceReleaseDate"
							class="my-2"
							style="border-color: #F1F1F1;"
						/>
						<p v-if="resourceReleaseDate">
                            <span class="file-page__dates-title">
                                Release date:
                            </span>
							<span class="file-page__dates-value">
                                {{ resourceReleaseDate }}
                            </span>
						</p>
						<v-divider
							v-if="resourceReleaseDate && resourceExpiryDate"
							class="my-2"
							style="border-color: #F1F1F1;"
						/>
						<p v-if="resourceExpiryDate">
                            <span class="file-page__dates-title">
                                Expiry date:
                            </span>
							<span class="file-page__dates-value">
                                {{ resourceExpiryDate }}
                            </span>
						</p>
					</div>
				</div>
			</div>
			<div class="file-page__error" v-else-if="errors">
				{{ errors.message }}
			</div>
		</div>
	</div>
</template>

<script>
import ResourceFileIcon from '../components/Resource/ResourceFileIcon.vue'
import Block from '@/mixins/Block.js'
import PageBlocked from '@/components/PageBlocked'
import { mapActions, mapGetters } from 'vuex'

export default {
	mixins: [Block],
	components: {
		ResourceFileIcon,
		PageBlocked,
	},
	data() {
		return {
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			tags: [
				'Mobile',
				'Member Engagement',
			],
			fileColors: [
				{ color: '#50bee8', ext: ['jpg', 'jpeg', 'psd'] },
				{ color: '#6678aa', ext: ['bmp'] },
				{ color: '#a066aa', ext: ['png', 'gif'] },
				{ color: '#f15642', ext: ['pdf'] },
				{ color: '#50bee8', ext: ['doc', 'docx'] },
				{ color: '#638674', ext: ['rtf'] },
				{ color: '#f15642', ext: ['ppt'] },
				{ color: '#84bd5a', ext: ['xls', 'xlsx'] },
				{ color: '#f7b84e', ext: ['mp3', 'wav', 'flac'] },
				{ color: '#f7b84e', ext: ['avi', 'mp4', 'mpeg'] },
				{ color: '#576d7e', ext: ['txt'] },
				{ color: '#e59f2b', ext: ['zip', 'rar', '7z', 'gz', 'gzip', 'bzip'] },
				{ color: '#41a8e1', ext: ['url'] },
			],
			pageBlockedName: 'resources',
		}
	},
	computed: {
		...mapGetters('resources', ['resource', 'isLoading', 'errors']),

		resourceImage() {
			return this.resource.resource_image
		},
		resourceUploadDate() {
			if (this.resource.upload && this.resource.upload.time) {
				return this.dateCreated(this.resource.upload.time)
			}
			return ''
		},
		resourceReleaseDate() {
			if (this.resource.resource_release_date) {
				return this.formatDate(this.resource.resource_release_date)
			}
			return ''
		},
		resourceExpiryDate() {
			if (this.resource.resource_expiry_date) {
				return this.formatDate(this.resource.resource_expiry_date)
			}
			return ''
		},
		datesSectionVisible() {
			return this.resourceUploadDate || this.resourceReleaseDate || this.resourceExpiryDate
		},
	},
	methods: {
		...mapActions('resources', ['getResourceById', 'clearResource']),

		dateCreated(originalDate) {
			const date = new Date(originalDate * 1000)
			const options = { year: 'numeric', month: 'short', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		formatDate(payload) {
			const date = new Date(payload * 1000)
			const options = {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			}
			const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date)
			return formattedDate
		},
	},
	mounted() {
		this.getResourceById(this.$route.params.id)
	},
	beforeDestroy() {
		this.clearResource()
	},
}
</script>

<style lang="scss">
.file-page {
	max-width: 600px;
	width: 100%;
	border-radius: 10px !important;
	margin-bottom: 30px;
	font-family: Montserrat, sans-serif;

	@media (max-width: 480px) {
		margin-bottom: 12px;
	}

	&__title {
		font-size: 24px;
		font-weight: 700;
		line-height: 29.26px;
		color: #4A4A4A;
	}

	&__body {
		&-header {
			display: flex;
			padding: 16px;
			background-color: #fff;
			border-radius: 8px;

			@media (max-width: 480px) {
				flex-direction: column;
			}

			&-img {
				width: 150px;
				height: 150px;
				border-radius: 8px;
				object-fit: contain;

				&-wrapper {
					margin-right: 8px;
					display: flex;
					justify-content: center;
					align-items: center;

					@media (max-width: 480px) {
						margin-bottom: 8px;
						justify-content: flex-start;
					}
				}
			}

			&-icon {
				&-wrapper {
					width: 150px;
					height: 150px;
					min-width: 150px;
					border-radius: 8px;
					background-color: #F3F3F6;
					display: flex;
					align-items: center;
					justify-content: center;

					img {
						mix-blend-mode: multiply;
					}

					@media (max-width: 480px) {
						margin-bottom: 12px;
					}
				}
			}
		}

		&-content {
			background-color: #fff;
			padding: 16px;
			border-radius: 8px;
		}
	}

	&__info {
		&-filename {
			font-size: 20px;
			font-weight: 700;
			line-height: 22px;
			color: var(--username-category-title);
			word-break: break-word;
			margin-bottom: 16px;
		}

		&-author {
			font-size: 13px;
			font-weight: 600;
			line-height: 18px;
			color: var(--company-position-large);
			margin-bottom: 10px;
		}

		&-btn {
			font-size: 16px !important;
			line-height: 19.5px;
			font-weight: 700;
			color: var(--button-main-color) !important;
			background-color: var(--button-main-background-color) !important;
			border-radius: 8px;
			text-transform: capitalize;
		}
	}

	&__text {
		font-size: 14px;
		line-height: 19px;
		color: #697583;
		margin-bottom: 18px !important;
	}

	&__dates {
		p {
			display: flex;
			justify-content: space-between;
			align-items: center;
			flex-wrap: nowrap;
			margin: 0;
		}

		&-title {
			font-size: 13px;
			font-weight: 600;
			color: #697583;
			line-height: 17px;
		}

		&-value {
			font-size: 13px;
			color: #697583;
			line-height: 22px;
		}
	}

	&__body-chips {
		margin-bottom: 16px;

		&-item {
			height: 24px !important;
			font-size: 12px !important;
			line-height: 16px;
			margin: 0 4px 4px 0;
		}
	}

	&__error {
		color: red;
	}
}
</style>